// import slugify from '@sindresorhus/slugify'
import { gql } from 'graphql-request';
import type {
	GetStaticPropsContext,
	NextPage,
	InferGetStaticPropsType,
	GetStaticPaths,
	GetStaticPathsResult,
} from 'next';
import Head from 'next/head';
import { renderMetaTags, useQuerySubscription } from 'react-datocms';
import { createQuerySubscription, fetchFromDatoCms } from '@datocms/api';
import {
	HERO_SECTION_FRAGMENT,
	LAYOUT_FRAGMENT,
	PAGE_SECTIONS_FRAGMENT,
	STRUCTURED_TEXT_LINKS_FRAGMENT,
	STRUCTURED_TEXT_PROSE_BLOCKS_FRAGMENT,
	PAGE_SEO_FRAGMENT,
	SITE_SEO_FRAGMENT,
} from '@datocms/fragments';
import LivePreviewLayout from '@datocms/LivePreviewLayout';
import Hero from '@sections/Hero';
import PageSection from '@sections/index';
import Prose, { OuterContainer, InnerContainer } from '@sections/Prose';
import usePageContext from 'hooks/usePageContext';

const pathsQuery = gql`
	query LocalizedSlugsQuery {
		allComposablePages {
			_allSlugLocales {
				locale
				value
			}
		}
	}
`;

const pageQuery = gql`
  query ComposablePageQuery($slug: String!, $locale: SiteLocale!) {
    ${SITE_SEO_FRAGMENT}
    page: composablePage(filter: {slug: {eq: $slug}}, locale: $locale) {
      _modelApiKey
      _allSlugLocales {
        locale
        value
      }
      ${PAGE_SEO_FRAGMENT}
      layout {
        ${LAYOUT_FRAGMENT}
      }
      compositionMode
      title
      heroSection {
        ${HERO_SECTION_FRAGMENT}
      }
      pageSections {
        ${PAGE_SECTIONS_FRAGMENT}
      }
      content {
        value
        links {
          ${STRUCTURED_TEXT_LINKS_FRAGMENT}
        }
        blocks {
          ${STRUCTURED_TEXT_PROSE_BLOCKS_FRAGMENT}
        }
      }
    }
  }
`;

export const getStaticPaths: GetStaticPaths = async () => {
	// this is forced to use the published endpoint, as no preview context is available. not ideal
	// getStaticPaths should be called with a preview context, but it's not available in this context
	const data = await fetchFromDatoCms({
		query: pathsQuery,
	});
	const paths: GetStaticPathsResult['paths'] = [];

	data.allComposablePages.forEach(({ _allSlugLocales }) =>
		_allSlugLocales.forEach(({ locale, value }) => {
			paths.push({ params: { slug: value }, locale });
		})
	);

	return {
		paths,
		fallback: 'blocking',
	};
};

export const getStaticProps = async ({
	locale,
	preview,
	params,
}: GetStaticPropsContext) => {
	const isPreview = preview || process.env.NODE_ENV === 'development';
	const querySubscriptionData = await createQuerySubscription<any>({
		query: pageQuery,
		variables: { slug: params?.slug, locale },
		preview: isPreview,
	});

	const page = querySubscriptionData?.initialData?.page;

	const isAvailableLocale = page?._allSlugLocales?.some(
		({ locale: availableLocale }) => availableLocale === locale
	);

	const props = {
		querySubscriptionData,
	};

	// this is a trick to make the page available in the preview context, but not in the production context
	// if a path exists in the preview context but not in the production context
	// the page will be rendered in the preview context, but will return a 404 in the production context
	if (!isAvailableLocale) {
		return {
			props,
			notFound: true,
		};
	}

	return { props };
};
const ComposablePage: NextPage<InferGetStaticPropsType<typeof getStaticProps>> =
	({ querySubscriptionData }) => {
		const { data, ...connection } = useQuerySubscription(querySubscriptionData);
		const { favicon } = data.site;
		const {
			compositionMode,
			pageSections = [],
			heroSection,
			content,
			title,
			seo,
		} = data.page;

		const isSections = compositionMode === 'sections';
		const { availableLocales, defaultLocale } = usePageContext(data);

		return (
			<>
				<Head>
					{availableLocales.map(([locale, slug]) => (
						<link
							key={locale}
							rel="alternate"
							hrefLang={locale}
							href={`${process.env.NEXT_PUBLIC_SITE_URL}${
								locale !== defaultLocale ? `/${locale}` : ''
							}/${slug}`}
						/>
					))}
					{renderMetaTags(seo.concat(favicon))}
				</Head>
				<LivePreviewLayout data={data} {...connection}>
					{heroSection?.[0] && (
						<Hero
							autoLinkTitle={true}
							dato={{
								...heroSection[0],
								...(!heroSection[0].title ? { title } : {}),
							}}
						/>
					)}
					{isSections ? (
						pageSections.map((typedSection) => (
							<PageSection key={typedSection.id} dato={typedSection} />
						))
					) : (
						<OuterContainer as="section">
							<InnerContainer>
								<Prose content={content} />
							</InnerContainer>
						</OuterContainer>
					)}
				</LivePreviewLayout>
			</>
		);
	};

export default ComposablePage;
